import { useCallback, useEffect, useMemo, useState } from "react";
import { Col } from "reactstrap";
import { useMediaQuery } from "react-responsive";
import { APIMS } from "../../../../../config/apims";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import Swal from "sweetalert2";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faListUl,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import Pagination from "../../../../components/pagination_new";

const isActiveStatus = (value) => {
  return value === true || Number(value) === 1;
};

// Gunakan tahun ajaran sebagai kunci, bukan nomor baris / semester.
const getAcademicYearKey = (row) => {
  const name = String(row?.name_year || "").trim();
  return name || String(row?.ysb_academic_year_id ?? "unknown");
};

export default function Semester() {
  document.title = "Semester";

  const [getData, setGetData] = useState([]);
  const ascending = 0;
  const token = localStorage.getItem("token");

  const id_user = localStorage
    .getItem("id_admin")
    ?.replace(/[|&;$%@"<>()+,]/g, "");

  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 780px)" });

 
  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);
  
  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const [id, setId] = useState();
  const [dataUpdate, setDataUpdate] = useState();
  const [statusLoadingId, setStatusLoadingId] = useState(null);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

  // =========================
  // GET DATA SEMESTER
  // =========================
  const GetResponseData = useCallback(async () => {
    setSearchKeyword("");
    setTablePage(1);
    setIsFetching(true);

    try {
      const response = await APIMS.get(
        "/api/semesters?page=1&limit=1000&ascending=1&search=",
        fetchParams
      );

      if (response?.status === 200) {
        setGetData(response?.data?.data || []);
      }
    } catch (error) {
      ToastError.fire({
        icon: "error",
        title:
          error.response?.data?.message ||
          "Gagal memuat data semester",
      });
    } finally {
      setIsFetching(false);
    }
  }, [ascending, fetchParams]);

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  // =========================
  // PAYLOAD UPDATE STATUS
  // =========================
  const buildSemesterPayload = useCallback(
    (row, status) => ({
      ysb_academic_year_id:
        row?.ysb_academic_year_id,

      name_year: row?.name_year || "",
      date_in: row?.date_in || "",
      date_out: row?.date_out || "",

      semester: Number(row?.semester),
      status: Number(status),

      user_id:
        id_user ||
        row?.user_id ||
        "",
    }),
    [id_user]
  );

  const updateSemesterStatus = useCallback(
    async (row, status) => {
      return APIMS.put(
        `/api/semesters/${row?.id}`,
        buildSemesterPayload(row, status),
        fetchParams
      );
    },
    [buildSemesterPayload, fetchParams]
  );

  // =========================
  // TOGGLE STATUS TABLE
  // =========================
  const handleToggleStatus = async (row) => {
    if (
      !row?.id ||
      statusLoadingId !== null
    ) {
      return;
    }

    const nextStatus = isActiveStatus(
      row?.status
    )
      ? 0
      : 1;

    const previousData = [...getData];

    const otherActiveRows =
      previousData.filter(
        (item) =>
          String(item?.id) !==
            String(row?.id) &&
          isActiveStatus(item?.status)
      );

    let previousRowsWereDisabled =
      false;

    setStatusLoadingId(row.id);

    // Optimistic update pada tampilan.
    setGetData((prev) =>
      prev.map((item) => {
        if (nextStatus === 1) {
          return {
            ...item,
            status:
              String(item?.id) ===
              String(row?.id)
                ? 1
                : 0,
          };
        }

        if (
          String(item?.id) ===
          String(row?.id)
        ) {
          return {
            ...item,
            status: 0,
          };
        }

        return item;
      })
    );

    try {
      // Jika semester akan diaktifkan,
      // semester aktif sebelumnya dimatikan.
      if (
        nextStatus === 1 &&
        otherActiveRows.length > 0
      ) {
        await Promise.all(
          otherActiveRows.map((item) =>
            updateSemesterStatus(item, 0)
          )
        );

        previousRowsWereDisabled = true;
      }

      await updateSemesterStatus(
        row,
        nextStatus
      );

      ToastSuccess.fire({
        icon: "success",
        title:
          nextStatus === 1
            ? "Semester berhasil diaktifkan"
            : "Semester berhasil dinonaktifkan",
      });

      await GetResponseData();
    } catch (error) {
      // Kembalikan semester aktif sebelumnya
      // jika proses gagal.
      if (previousRowsWereDisabled) {
        try {
          await Promise.all(
            otherActiveRows.map((item) =>
              updateSemesterStatus(item, 1)
            )
          );
        } catch (rollbackError) {
          console.error(
            "Gagal mengembalikan semester aktif sebelumnya:",
            rollbackError
          );
        }
      }

      setGetData(previousData);

      ToastError.fire({
        icon: "error",
        title:
          error.response?.data?.message ||
          "Gagal mengubah status semester",
      });

      await GetResponseData();
    } finally {
      setStatusLoadingId(null);
    }
  };

  // =========================
  // DELETE
  // =========================

  const deleteById = async (id, year, semester) => {
    Swal.fire({
      title: "Hapus data ini?",
      html: `<p class="sims-delete-description">Data <strong></strong> yang dihapus tidak dapat dikembalikan.</p>`,
      icon: "warning",
      customClass: {
        popup: "sims-delete-popup",
        icon: "sims-delete-icon",
        title: "sims-delete-title",
        actions: "sims-delete-actions",
        confirmButton: "sims-delete-confirm",
        cancelButton: "sims-delete-cancel",
        validationMessage: "sims-delete-validation",
      },
      buttonsStyling: false,
      showCancelButton: true,
      confirmButtonText: "Hapus data",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,

      didOpen: (popup) => {
        // popup.querySelector(".sims-delete-description strong").textContent = name || "Semester";
        popup.querySelector(".sims-delete-description strong").textContent = [year, semester || "Semester"].join(" - Semester ");
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APIMS.delete(
            `/api/semesters/${id}`,
            fetchParams
          );
  
          if (response.data.error) {
            throw new Error(response.data.message);
          }
  
          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error.response?.data?.message || "Gagal menghapus data"
          );
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        ToastSuccess.fire({
          icon: "success",
          title: "Data berhasil dihapus",
        });
        GetResponseData();
      }
    });
  };

  // =========================
  // MODAL UPDATE
  // =========================
  const viewModalUpdate = (id, semester) => {
    setModalUpdate(true)
    setId(id)
    setDataUpdate(semester)
  }

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );
  
// =========================
// SEARCH FRONTEND
// =========================
const filteredData = useMemo(() => {
  const keyword = String(searchKeyword || "")
    .trim()
    .toLowerCase();

  const rows = !keyword
    ? getData || []
    : (getData || []).filter((item) => {
        const nameYear = String(
          item?.name_year || ""
        ).toLowerCase();

        const semester = String(
          item?.semester || ""
        ).toLowerCase();

        const statusText = isActiveStatus(
          item?.status
        )
          ? "aktif"
          : "tidak aktif";

        return (
          nameYear.includes(keyword) ||
          semester.includes(keyword) ||
          statusText.includes(keyword)
        );
      });

  return [...rows].sort((a, b) => {
    // =========================
    // TAHUN AJARAN TERBARU DI ATAS
    // contoh:
    // 2027/2028
    // 2026/2027
    // 2025/2026
    // =========================
    const yearCompare = nameCollator.compare(
      String(b?.name_year || "").trim(),
      String(a?.name_year || "").trim()
    );

    if (yearCompare !== 0) {
      return yearCompare;
    }

    // =========================
    // DALAM TAHUN AJARAN YANG SAMA:
    // Semester 1 dulu
    // Semester 2 setelahnya
    // =========================
    return (
      Number(a?.semester || 0) -
      Number(b?.semester || 0)
    );
  });
}, [
  getData,
  searchKeyword,
  nameCollator,
]);

  // Warna tetap konsisten ketika pencarian atau halaman berubah.
  const academicYearGroups = useMemo(() => {
    const years = [...new Set((getData || []).map(getAcademicYearKey))];
    years.sort((a, b) => nameCollator.compare(b, a));
    return new Map(years.map((year, index) => [year, index]));
  }, [getData, nameCollator]);

  // =========================
  // PAGINATION FRONTEND
  // =========================
  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, tablePage, rowsPerPage]);


  // =========================
  // FORMAT TANGGAL
  // =========================
  const formatTanggal = (dateString) => {
    if (!dateString) {
      return "-";
    }

    const cleanDate = String(dateString).slice(0, 10);

    const [tahun, bulan, tanggal,] = cleanDate.split("-");

    if (
      !tahun || !bulan || !tanggal) {
      return dateString;
    }

    return `${tanggal}-${bulan}-${tahun}`;
  };

  return (
    <div className="body sims-page sims-semester-page">
      <style>{`
        .sims-semester-page .sims-table tbody tr.semester-year-soft > td {
          background-color: rgba(247, 248, 255, 0.46) !important;
        }
        .sims-semester-page .sims-table tbody tr.semester-year-white > td {
          background-color: #ffffff !important;
        }
        .sims-semester-page .sims-table tbody tr.semester-year-start > td {
          border-top: 2px solid #dce5f5 !important;
        }
        .sims-semester-page .sims-table tbody tr.semester-year-soft:hover > td {
          background-color: #edf3ff !important;
        }
        .sims-semester-page .sims-table tbody tr.semester-year-white:hover > td {
          background-color: #f8faff !important;
        }
      `}</style>
      {modalAdd && (
        <ModalAdd
          GetResponseData={ GetResponseData}
          semesterData={getData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          semesterData={getData}
          id={id}
          dataUpdate={dataUpdate}
          show={modalUpdate}
          onHide={() => setModalUpdate(false)}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon icon={faListUl} />
            </span>
            <h4 className="page-title-text"> Data Semester </h4>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              className="btn btn-create sims-header-button"
              onClick={() => setModalAdd(true)}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>{isTabletOrMobile ? "Semester" : "Tambah Semester"}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {isFetching ? (
          <div className="sims-loading">
            <PulseLoader color="#3f5bea" size={12} />
          </div>
        ) : (
          <Col xl="12" sm="12" className="sims-column">
            <div className="sims-search-wrap">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />

              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari semester..."
                value={searchKeyword}
                onChange={(event) => {
                  setSearchKeyword(event.target.value);
                  setTablePage(1);
                }}
              />
            </div>

            <div className="sims-table-card mt-3">
                <div className="sims-table-scroll">
                  <table className="sims-table">
                    <thead>
                      <tr>
                        <th className="sims-col-number">
                          No
                        </th>
                        <th> Tahun Ajaran </th>
                        <th> Semester  </th>
                        <th> Tanggal Mulai </th>
                        <th> Tanggal Akhir </th>

                        <th className="sims-col-status">
                          Status
                        </th>

                        <th className="sims-col-action">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedData.length >
                      0 ? (
                        paginatedData.map(
                          ( row, index ) => {
                            const active =
                              isActiveStatus(
                                row?.status
                              );

                            const isStatusLoading =
                              String(
                                statusLoadingId
                              ) ===
                              String(
                                row?.id
                              );

                            const yearKey = getAcademicYearKey(row);
                            const yearGroupIndex = academicYearGroups.get(yearKey) ?? 0;
                            const isNewYearGroup =
                              index > 0 &&
                              yearKey !== getAcademicYearKey(paginatedData[index - 1]);

                            return (
                              <tr
                                key={row?.id || index}
                                className={`${yearGroupIndex % 2 === 0 ? "semester-year-soft" : "semester-year-white"}${isNewYearGroup ? " semester-year-start" : ""}`}
                              >
                                <td className="sims-col-number">
                                  <span className="sims-number-badge">
                                    {(tablePage - 1) * rowsPerPage + index + 1}
                                  </span>
                                </td>

                                 <td>
                                  <span  className="sims-text-bold" >
                                    {row?.name_year ||
                                      "-"}
                                  </span>
                                </td>


                                <td>
                                  <span className="sims-text-bold">
                                    Semester{" "}
                                    {row?.semester ||
                                      "-"}
                                  </span>
                                </td>

                                <td>
                                  <span className="sims-muted-text">
                                    {formatTanggal(
                                      row?.date_in
                                    )}
                                  </span>
                                </td>

                                <td>
                                  <span className="module-muted-text">
                                    {formatTanggal(
                                      row?.date_out
                                    )}
                                  </span>
                                </td>

                                <td className="sims-col-status">
                                  <div className="sims-status-control">
                                    <label
                                      className={`sims-toggle ${
                                        isStatusLoading
                                          ? "is-loading"
                                          : ""
                                      }`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={
                                          active
                                        }
                                        disabled={
                                          statusLoadingId !==
                                          null
                                        }
                                        onChange={() =>
                                          handleToggleStatus(
                                            row
                                          )
                                        }
                                        aria-label={`Ubah status ${
                                          row?.name_year ||
                                          "semester"
                                        } semester ${
                                          row?.semester ||
                                          ""
                                        }`}
                                      />

                                      <span className="sims-toggle-slider" />
                                    </label>

                                    <span
                                      className={`sims-status-label ${
                                        active
                                          ? "is-active"
                                          : "is-inactive"
                                      }`}
                                    >
                                      {isStatusLoading
                                        ? "Menyimpan..."
                                        : active
                                        ? "Aktif"
                                        : "Tidak Aktif"}
                                    </span>
                                  </div>
                                </td>

                                <td className="sims-col-action">
                                  <div className="sims-action-wrap">
                                    <button
                                      type="button"
                                      className="sims-action-button module-action-edit"
                                      disabled={
                                        statusLoadingId !==
                                        null
                                      }
                                      onClick={() =>
                                        viewModalUpdate(
                                          row?.id,
                                          row
                                        )
                                      }
                                      aria-label="Edit semester"
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          faPenToSquare
                                        }
                                      />
                                    </button>

                                    <button
                                      type="button"
                                      className="sims-action-button sims-action-delete"
                                      disabled={
                                        statusLoadingId !==
                                        null
                                      }
                                      onClick={() =>
                                        deleteById(
                                          row?.id,
                                          row?.name_year,
                                          row?.semester
                                        )
                                      }
                                      aria-label="Hapus semester"
                                    >
                                      <FontAwesomeIcon
                                        icon={
                                          faTrash
                                        }
                                      />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }
                        )
                      ) : (
                        <tr>
                          <td
                            colSpan="7"
                            className="sims-empty-state"
                          >
                            <div className="sims-empty-state-content">
                              Data semester tidak ditemukan
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

              <Pagination
                totalRows={filteredData.length}
                tablePage={tablePage}
                rowsPerPage={rowsPerPage}
                onPageChange={setTablePage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setTablePage(1);
                }}
              />
            </div>
          </Col>
        )}
      </div>
    </div>
  );
}